import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";

import { api, HttpError } from "../api/rest";
import type { StrategyMode, StrategyRole, StrategySettingsPatch } from "../api/types";
import { useStatus } from "../hooks/useStatus";
import { FOLLOW_MODES, modeHint, modeLabel, raisesRisk } from "../lib/signals";
import {
  modeWarning,
  roleAllowed,
  roleHint,
  roleLabel,
  STRATEGY_ROLES,
  strategyModeAllowed,
  strategySummary,
} from "../lib/strategies";
import { isStrategySubsystem } from "../lib/subsystems";
import { SubsystemAccountSelect } from "./SubsystemAccountSelect";

/**
 * Управление живым режимом ОДНОЙ стратегии.
 *
 * # Почему режим — текст, а настройки спрятаны
 *
 * Читают эту строку каждый раз, а трогают её редко: режим стои́т неделями, а
 * смотрят на него при каждом взгляде на таблицу замера. Ряд кнопок на виду
 * отвечал на вопрос «что я могу нажать», хотя спрашивают здесь другое — «что
 * сейчас действует». Теперь снаружи ровно ответ на второй вопрос, словами
 * трейдера, а орудия — за кнопкой «настроить».
 *
 * Раскрытие сделано на месте, а не всплывающим слоем: панели стратегий сами
 * складываются и режут содержимое по краю, и всплывающий слой обрезался бы
 * ровно в тот момент, когда он открыт.
 *
 * # Почему внутри панели своей стратегии
 *
 * Решение о режиме принимается по числам замера — по той же таблице, над которой
 * этот блок и стоит. Разведи их по разным экранам, и оператору пришлось бы
 * помнить цифру, переходя по ссылке.
 *
 * # Асимметрия подтверждения
 *
 * Понизить — один клик, поднять — два, и второй шаг сам себя отменяет через
 * CONFIRM_MS. Ошибка в сторону «меньше торговли» стоит пропущенных сигналов,
 * ошибка в другую сторону стоит денег. Тот же приём и то же число, что у
 * ScenarioModeSwitch: два разных срока подтверждения на одном экране означали бы
 * две разные привычки руки.
 *
 * # Почему потолок окружения назван вслух
 *
 * Потому что «кнопка не нажимается» и «кнопка не нажимается ПОТОМУ ЧТО» — это
 * разница между поломкой и правилом. При `DECIDED_MODE`, не выставленном в
 * `.env`, потолок равен `shadow`, и живой режим из браузера не поднять вовсе:
 * ключ оператора умеет только понижать. Подсказка в `title` этого не говорила —
 * её надо было найти наведением на выключенную кнопку.
 *
 * # Возврат к потолку окружения
 *
 * У каждого ключа оператора есть кнопка «снять»: она пишет ПУСТУЮ строку, и
 * трейдер читает такое значение как отсутствие ключа (livecfg.pass.read), то
 * есть величину снова решает потолок `.env`. Без неё первая же запись была бы
 * необратимой из браузера — «понизил ставку до $5» держалось бы до похода в
 * `redis-cli`, потому что числа, означающего «мой потолок больше не действует»,
 * у суммы не существует.
 *
 * Режима среди снимаемых НЕТ, и это не пропуск. Пустой `strategy:<id>:mode`
 * трейдер читает не как отсутствие ключа, а как неразобранное значение — и
 * выключает стратегию целиком (livecfg.Effective, fail-closed). Кнопка «снять»
 * у режима обещала бы потолок окружения, а выдавала бы `off`. Потери при этом
 * никакой: у режима «ключа нет» и «выставлено auto» дают ОДНО действующее
 * значение, потому что оно строгий минимум с потолком, — вернуть решение
 * окружению можно выбором, а суммам нечем.
 *
 * # Что здесь НЕ считается
 *
 * Действующий режим. Он приезжает из GET /status полем `strategies` — строкой,
 * как её называет ТРЕЙДЕР. Посчитай интерфейс минимум сам, и на экране появилось
 * бы второе определение действующего режима.
 */
const CONFIRM_MS = 5000;

/** Сколько держится отметка «сохранено» после удачной записи. */
const SAVED_MS = 2000;

/**
 * Снятие ключа роли — та же пустая строка, что и у сумм.
 *
 * Приведение типа здесь единственный честный выход: `StrategyRole` —
 * перечисление четырёх ролей, и пустоты в нём нет намеренно. Впусти её в
 * перечисление ради этой кнопки — и «роль не выбрана» стало бы допустимым
 * значением ВЕЗДЕ, включая разбор ноты трейдера, где та же пустая строка
 * означает совсем другое: роль, которую не удалось прочитать.
 */
const CLEAR_ROLE = "" as StrategyRole;

export function StrategyLiveControls({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const status = useStatus();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<StrategyMode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ["strategy-settings", id],
    queryFn: () => api.strategySettings(id),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setPending(null), CONFIRM_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);

  const st = status.data?.strategies?.find((s) => s.id === id);
  const ceiling = st?.mode_env;
  const s = settings.data?.settings;
  const current = s?.mode ?? null;
  const modeSet = s?.mode_set ?? false;
  const role = s?.role ?? null;
  const roleSet = s?.role_set ?? false;
  const priceSet = s?.price_set ?? false;
  const sourceSet = s?.source_set ?? false;

  async function write(patch: StrategySettingsPatch) {
    setError(null);
    try {
      await api.setStrategySettings(id, patch);
      void queryClient.invalidateQueries({ queryKey: ["strategy-settings", id] });
      // Действующий режим считает трейдер, и увидеть его можно только через
      // /status: без этого сброса подпись отставала бы на такт опроса и
      // показывала прежний режим по уже нажатой кнопке.
      void queryClient.invalidateQueries({ queryKey: ["status"] });
    } catch (err) {
      setError(err instanceof HttpError ? err.message : "не удалось записать настройку");
      throw err;
    }
  }

  async function applyMode(next: StrategyMode) {
    if (raisesRisk(current, next) && pending !== next) {
      setPending(next);
      return;
    }
    setPending(null);
    await write({ mode: next }).catch(() => {});
  }

  // Ключи оператора перечисляются и в свёрнутом виде: иначе понижение, спрятанное
  // за кнопкой, было бы невидимо ровно тем взглядом, которым эту строку и читают,
  // а действующее значение отличалось бы от потолка без объяснения.
  const overridden = [
    modeSet && "режим",
    roleSet && "роль",
    (s?.order_usd_set ?? false) && "ставка",
    (s?.daily_cap_usd_set ?? false) && "за сутки",
    priceSet && "цена",
    sourceSet && "источник",
  ].filter((v): v is string => typeof v === "string");

  return (
    <div className="flex flex-col gap-1 border-t border-[var(--color-border)] pt-2 text-[11px]">
      {/* Действующий режим — словами трейдера. Причина обязательна: «shadow»
          одинаково выглядит при выключенной кнопке, при потолке окружения и при
          неотвечающем Redis, а чинится это по-разному. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[var(--color-muted)]">живой режим</span>
        <span>{strategySummary(st)}</span>
        {overridden.length > 0 && (
          <span
            className="text-[var(--color-muted)]"
            title="величины, которые понижены ключом оператора, а не потолком окружения"
          >
            ключи: {overridden.join(", ")}
          </span>
        )}
        <button
          type="button"
          aria-expanded={open}
          aria-label="настроить живой режим"
          onClick={() => setOpen((v) => !v)}
          className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[var(--color-muted)]"
        >
          {open ? "свернуть" : "настроить"}
        </button>
      </div>

      {/* Аккаунт — вне свёрнутых настроек: режим «торговать» без него не
          торгует, и это должно быть видно, не раскрывая блок. */}
      {isStrategySubsystem(id) && <SubsystemAccountSelect subsystem={id} />}

      {open && (
        <div className="flex flex-col gap-2 rounded border border-[var(--color-border)] p-2">
          <CeilingNote ceiling={ceiling} />

          <Row label="режим">
            {FOLLOW_MODES.map((m) => {
              const allowed = strategyModeAllowed(m, ceiling);
              // «Выставлено здесь» и «действует потолок» — разные состояния, и
              // подсветка обязана их различать: иначе оператор не отличит режим,
              // который он сам понизил, от того, который никогда не поднимался.
              const active = modeSet && current === m;
              const warn = modeWarning(m);
              return (
                <button
                  key={m}
                  type="button"
                  disabled={!allowed}
                  onClick={() => void applyMode(m)}
                  title={allowed ? (warn && pending === m ? warn : modeHint(m)) : undefined}
                  className={`rounded border px-1.5 py-0.5 disabled:opacity-40 ${
                    active
                      ? "border-[var(--color-accent)] text-[var(--color-accent)]"
                      : "border-[var(--color-border)] text-[var(--color-muted)]"
                  }`}
                >
                  {pending === m ? "точно?" : modeLabel(m)}
                </button>
              );
            })}
          </Row>

          <Row label="роль">
            {STRATEGY_ROLES.map((r) => {
              const allowed = roleAllowed(r, ceiling);
              const active = roleSet && role === r;
              return (
                <button
                  key={r}
                  type="button"
                  disabled={!allowed}
                  onClick={() => void write({ role: r }).catch(() => {})}
                  title={allowed ? roleHint(r) : undefined}
                  className={`rounded border px-1.5 py-0.5 disabled:opacity-40 ${
                    active
                      ? "border-[var(--color-accent)] text-[var(--color-accent)]"
                      : "border-[var(--color-border)] text-[var(--color-muted)]"
                  }`}
                >
                  {roleLabel(r)}
                </button>
              );
            })}
            {roleSet && (
              <ClearButton what="роль" onClear={() => write({ role: CLEAR_ROLE })} />
            )}
          </Row>

          <BudgetField
            label="ставка"
            value={s?.order_usd}
            set={s?.order_usd_set ?? false}
            note={`ставка одного ордера; сейчас действует $${st?.order_live ?? "?"}, поднять выше $${st?.order_usd ?? "потолка"} нельзя`}
            onWrite={(v) => write({ order_usd: v })}
          />
          <BudgetField
            label="за сутки"
            value={s?.daily_cap_usd}
            set={s?.daily_cap_usd_set ?? false}
            note="дневной оборот стратегии; вводом можно только понизить"
            onWrite={(v) => write({ daily_cap_usd: v })}
          />

          {/* Клетка журнала. Показывается ТОЛЬКО когда ключ выставлен — потому
              что действующую клетку сказать нечем: в /status её нет, а из двух
              стратегий она есть у одной (у family клетки нет вовсе). Строка
              «цена: потолок» на экране family была бы обещанием настройки,
              которой у неё не существует. */}
          {priceSet && (
            <ClearableKey
              label="цена"
              value={s?.price}
              note="ступень лесенки, которой разрешено торговать; ступень вне лесенки трейдер игнорирует. Выставляется только в окружении"
              onClear={() => write({ price: "" })}
            />
          )}
          {sourceSet && (
            <ClearableKey
              label="источник"
              value={s?.source}
              note="источник ряда — вторая половина клетки журнала. Выставляется только в окружении"
              onClear={() => write({ source: "" })}
            />
          )}
        </div>
      )}

      {error && <div className="text-[var(--color-sell)]">{error}</div>}
    </div>
  );
}

/** Строка настройки: подпись слева, орудия справа. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="w-16 shrink-0 text-[var(--color-muted)]">{label}</span>
      {children}
    </div>
  );
}

/**
 * Потолок окружения словами, а не выключенной кнопкой.
 *
 * Отдельной строкой, потому что он объясняет ВСЮ панель разом: и почему режим
 * выше не выбирается, и почему роль сужена, и почему понижение суммы — это всё,
 * что отсюда можно сделать. Повтори это подсказкой на каждой кнопке, и оператору
 * пришлось бы навести на четыре, чтобы понять одно правило.
 */
function CeilingNote({ ceiling }: { ceiling: string | undefined }) {
  if (!ceiling) {
    return (
      <div className="text-[var(--color-sell)]">
        потолок окружения неизвестен: трейдер не отвечает. Пока он молчит, ключи
        оператора записать можно, но действовать они начнут только после ответа.
      </div>
    );
  }
  const frozen = !strategyModeAllowed("alert", ceiling);
  return (
    <div className="text-[var(--color-muted)]">
      окружение разрешает не выше: <span className="text-[var(--color-fg)]">{ceiling}</span>.
      Ключ оператора умеет только ПОНИЖАТЬ — действующее значение есть строгий
      минимум ключа и потолка.
      {frozen && (
        <>
          {" "}
          Поднять живой режим отсюда нельзя: разрешение выдаётся в `.env`
          (`&lt;СТРАТЕГИЯ&gt;_MODE` и `_ROLE`) и требует рестарта трейдера.
        </>
      )}
    </div>
  );
}

/**
 * Поле суммы: ввод понижает, кнопка рядом снимает.
 *
 * # Почему запись идёт и по уходу из поля
 *
 * Потому что Enter — не единственный способ закончить ввод, а был единственным
 * способом записать. Набрать число и кликнуть мимо значило потерять его молча:
 * ни записи, ни отказа, ни следа на экране. Уход из поля — такое же завершение
 * ввода, и понижение суммы, случившееся по нему, идёт в БЕЗОПАСНУЮ сторону:
 * поднять этим ключом нельзя ничего.
 *
 * # Почему в поле стои́т значение, а не плейсхолдер
 *
 * Прежде после записи поле очищалось, и величина оставалась только
 * плейсхолдером — то есть выглядела ровно как ненастроенное поле. «Сохранил и
 * увидел пустоту» читается как «не сохранилось», и отличить одно от другого
 * было нечем. Теперь поле показывает ключ, отметка «сохранено» подтверждает
 * запись, а `Escape` возвращает поле к сохранённому значению.
 *
 * Пустое поле НЕ снимает ключ: «понизить» и «вернуть потолок» — про разные
 * деньги, а очистка поля выглядит одинаково при обеих задумках и при отсутствии
 * всякой. Снятие названо кнопкой рядом. Ноль не отправляется тем более: его
 * отвергает и ручка (400), и сам трейдер (неположительная сумма оставляет
 * потолок), так что «понизить до нуля» не существует ни на одном конце.
 * Торговлю выключает режим `off`.
 */
function BudgetField({
  label,
  value,
  set,
  note,
  onWrite,
}: {
  label: string;
  value: string | undefined;
  set: boolean;
  note: string;
  onWrite: (v: string) => Promise<void>;
}) {
  const saved = set && value ? value : "";
  const [draft, setDraft] = useState(saved);
  const [dirty, setDirty] = useState(false);
  const [flash, setFlash] = useState(false);
  // Серверное значение на момент СВОЕЙ записи. Пока ответ ручки не доехал
  // опросом, поле держит записанное, а не откатывается к нему же прежнему.
  const [held, setHeld] = useState<string | null>(null);

  // Пока поле не правят, оно следует за ключом: его могли сменить из другой
  // вкладки, и показывать своё устаревшее значение поверх чужой записи хуже,
  // чем не показывать ничего.
  //
  // Кроме одного окна: между удачной записью и приездом нового GET. Ключ в
  // ответе ещё старый, и следование за ним показало бы прежнюю сумму — то есть
  // ровно «не сохранилось» поверх состоявшейся записи. Хуже всего это на
  // скрытой вкладке, где опрос не идёт вовсе и окно длится до возвращения
  // оператора. Держим своё, пока сервер не скажет ЧТО-НИБУДЬ иное: сказанное
  // им может отличаться и от записанного (нормализация суммы), и следовать
  // дальше надо за ним.
  useEffect(() => {
    if (dirty) return;
    if (held !== null) {
      if (saved !== held) setHeld(null);
      return;
    }
    setDraft(saved);
  }, [saved, dirty, held]);

  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(false), SAVED_MS);
    return () => window.clearTimeout(timer);
  }, [flash]);

  async function commit() {
    if (!dirty) return;
    const v = draft.trim();
    // Пустое поле — отмена правки, а не команда: возвращаем сохранённое.
    if (!v || v === saved) {
      setDraft(saved);
      setDirty(false);
      return;
    }
    try {
      await onWrite(v);
      setHeld(saved);
      setDraft(v);
      setDirty(false);
      setFlash(true);
    } catch {
      // Текст отказа показывает общий блок ошибки; правка остаётся в поле,
      // чтобы её можно было исправить, а не набирать заново.
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="w-16 shrink-0 text-[var(--color-muted)]">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        placeholder="потолок"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setDirty(true);
        }}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            void commit();
            return;
          }
          if (e.key === "Escape") {
            setDraft(saved);
            setDirty(false);
          }
        }}
        className="w-16 rounded border border-[var(--color-border)] bg-transparent px-1 py-0.5 text-right"
      />
      {dirty && <span className="text-[var(--color-muted)]">Enter</span>}
      {flash && <span className="text-[var(--color-accent)]">сохранено</span>}
      {set && <ClearButton what={label} onClear={() => onWrite("")} />}
      <span className="text-[var(--color-muted)]">{note}</span>
    </div>
  );
}

/**
 * Ключ, который этот экран не умеет ВЫСТАВЛЯТЬ, но обязан уметь снимать.
 *
 * Клетку журнала (цену и источник) выбирают вне браузера: допустимые значения
 * знает потолок окружения стратегии — лесенка `DECIDED_LIMIT_PRICES` и список
 * источников, — а сюда они не приезжают, и поле ввода предлагало бы ступени,
 * которые трейдер молча проигнорирует. Снять же выставленный ключ можно не зная
 * лесенки: пустая строка одинаково законна при любой.
 *
 * Значение показывается рядом ровно затем, чтобы снимали не вслепую, а слово
 * «нечитаемо» на его месте — затем, чтобы испорченный ключ не выглядел
 * отсутствующим: ручка отдаёт «выставлено» и без разобранной величины, и такой
 * ключ снимать как раз и надо.
 */
function ClearableKey({
  label,
  value,
  note,
  onClear,
}: {
  label: string;
  value: string | undefined;
  note: string;
  onClear: () => Promise<void>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="w-16 shrink-0 text-[var(--color-muted)]">{label}</span>
      <span>{value ?? "нечитаемо"}</span>
      <ClearButton what={label} onClear={onClear} />
      <span className="text-[var(--color-muted)]">{note}</span>
    </div>
  );
}

/**
 * Кнопка «снять ключ»: величину снова решает потолок окружения.
 *
 * # Почему кнопка, а не очистка поля
 *
 * Потому что снятие обязано быть НАЗВАНО. «Понизить» и «вернуть потолок» — про
 * разные деньги, а пустое поле выглядит одинаково при обеих задумках и при
 * отсутствии всякой (см. BudgetField). Кнопка со словом на ней не путается ни с
 * чем и не срабатывает привычным Enter.
 *
 * # Почему только при выставленном ключе
 *
 * Кнопка отвечает и на второй вопрос — «а ключ вообще мой?». Снятие
 * несуществующего ключа не делает ничего, и кнопка, стоящая на экране всегда,
 * обещала бы действие там, где действовать не над чем.
 *
 * # Почему два нажатия
 *
 * Потому что снятие — это ПОВЫШЕНИЕ. Действующее значение есть строгий минимум
 * ключа и потолка, значит потолок не ниже снимаемого ключа, и после снятия
 * подсистема получит сумму больше прежней. Та же асимметрия, тот же CONFIRM_MS
 * и та же надпись «точно?», что у повышения режима: на одном экране у руки одна
 * привычка.
 */
function ClearButton({ what, onClear }: { what: string; onClear: () => Promise<void> }) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), CONFIRM_MS);
    return () => window.clearTimeout(timer);
  }, [armed]);

  return (
    <button
      type="button"
      // Подпись для чтения с экрана и для теста: на кнопке стоит одно слово, и
      // «снять» без имени ключа не отвечает, что именно снимают.
      aria-label={`снять ключ: ${what}`}
      title={`вернуть «${what}» к потолку окружения: ключ оператора перестанет действовать`}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        void onClear().catch(() => {});
      }}
      className={`rounded border px-1.5 py-0.5 ${
        armed
          ? "border-[var(--color-accent)] text-[var(--color-accent)]"
          : "border-[var(--color-border)] text-[var(--color-muted)]"
      }`}
    >
      {armed ? "точно?" : "снять"}
    </button>
  );
}
